import classNames from 'classnames';
import React from 'react';

import Heading from '@jpp/atoms/Heading';
import Container from '@jpp/shared/Grid/Container';
import Flex from '@jpp/shared/Grid/Flex';
import { IFlexProps } from '@jpp/shared/Grid/Flex/Flex';
import Row from '@jpp/shared/Grid/Row';
import Section from '@jpp/shared/Grid/Section';
import { WYSIWYG } from '@jpp/shared/WYSIWYG/WYSIWYG';
import { IPdpBeerInfo } from 'common/redux/pdp/reducer';
import { EProductType } from 'common/typings/enums/product';

import './PDPInformation.scss';

interface IPDPInformationProps {
  className?: string;
  content: string;
  beerInfo: IPdpBeerInfo;
  pdpType: EProductType;
  shouldRenderBeerInfo?: boolean;
}

const ROOT_CLASSNAME = 'PDPInformation';

const getTitle = (pdpType: EProductType): string => {
  if (pdpType === EProductType.GiftCard) {
    return 'How to redeem';
  }

  if (pdpType === EProductType.Subscription) {
    return 'Subscription Details';
  }

  if (pdpType === EProductType.Merchandise) {
    return 'About this product';
  }

  return 'About this beer';
};

const PDPInformation: React.FunctionComponent<IPDPInformationProps> = ({
  className,
  content,
  shouldRenderBeerInfo = false,
  beerInfo,
  pdpType,
}) => {
  const {
    abv,
    allergens,
    aroma_hops,
    bittering_hops,
    style,
    yeast,
    malt,
    sold_as,
  } = beerInfo || {};
  const soldAsLength = sold_as?.length || 0;

  const columnProps: Partial<IFlexProps> = {
    colLg: '8',
    colMd: '10',
    className: 'mx-auto',
  };

  return (
    <Section
      className={classNames(ROOT_CLASSNAME, className)}
      withPadding={false}
    >
      <Container>
        <Row>
          <Flex {...columnProps}>
            {content && (
              <div className={`${ROOT_CLASSNAME}__group`}>
                <Heading
                  className={`${ROOT_CLASSNAME}__heading`}
                  priority={2}
                  children={getTitle(pdpType)}
                />

                <WYSIWYG
                  className={`${ROOT_CLASSNAME}__content`}
                  content={content}
                />
              </div>
            )}

            {beerInfo && shouldRenderBeerInfo && (
              <div className={`${ROOT_CLASSNAME}__group`}>
                <Heading
                  className={`${ROOT_CLASSNAME}__heading`}
                  priority={2}
                  children="Beer info"
                />

                <table className={`${ROOT_CLASSNAME}__table`}>
                  <tbody className={`${ROOT_CLASSNAME}__tbody`}>
                    <tr className={`${ROOT_CLASSNAME}__table-row`}>
                      <th className={`${ROOT_CLASSNAME}__table-heading`}>
                        Style:&nbsp;
                      </th>
                      <td className={`${ROOT_CLASSNAME}__table-cell`}>
                        {style}
                      </td>
                    </tr>
                    <tr className={`${ROOT_CLASSNAME}__table-row`}>
                      <th className={`${ROOT_CLASSNAME}__table-heading`}>
                        ABV:&nbsp;
                      </th>
                      <td className={`${ROOT_CLASSNAME}__table-cell`}>
                        {abv}%
                      </td>
                    </tr>
                    {aroma_hops ? (
                      <tr className={`${ROOT_CLASSNAME}__table-row`}>
                        <th className={`${ROOT_CLASSNAME}__table-heading`}>
                          Aroma&nbsp;hops:&nbsp;
                        </th>
                        <td className={`${ROOT_CLASSNAME}__table-cell`}>
                          {aroma_hops}
                        </td>
                      </tr>
                    ) : null}
                    {bittering_hops ? (
                      <tr className={`${ROOT_CLASSNAME}__table-row`}>
                        <th className={`${ROOT_CLASSNAME}__table-heading`}>
                          Bittering&nbsp;hops:&nbsp;
                        </th>
                        <td className={`${ROOT_CLASSNAME}__table-cell`}>
                          {bittering_hops}
                        </td>
                      </tr>
                    ) : null}
                    {yeast ? (
                      <tr className={`${ROOT_CLASSNAME}__table-row`}>
                        <th className={`${ROOT_CLASSNAME}__table-heading`}>
                          Yeast:&nbsp;
                        </th>
                        <td className={`${ROOT_CLASSNAME}__table-cell`}>
                          {yeast}
                        </td>
                      </tr>
                    ) : null}
                    {malt ? (
                      <tr className={`${ROOT_CLASSNAME}__table-row`}>
                        <th className={`${ROOT_CLASSNAME}__table-heading`}>
                          Malt:&nbsp;
                        </th>
                        <td className={`${ROOT_CLASSNAME}__table-cell`}>
                          {malt}
                        </td>
                      </tr>
                    ) : null}
                    {allergens ? (
                      <tr className={`${ROOT_CLASSNAME}__table-row`}>
                        <th className={`${ROOT_CLASSNAME}__table-heading`}>
                          Allergens:&nbsp;
                        </th>
                        <td className={`${ROOT_CLASSNAME}__table-cell`}>
                          {allergens}
                        </td>
                      </tr>
                    ) : null}
                    {soldAsLength > 0 ? (
                      <tr className={`${ROOT_CLASSNAME}__table-row`}>
                        <th className={`${ROOT_CLASSNAME}__table-heading`}>
                          Sold as:&nbsp;
                        </th>
                        <td className={`${ROOT_CLASSNAME}__table-cell`}>
                          {sold_as.map((sold, index) => {
                            const isLast = soldAsLength === index + 1;
                            return (
                              <span
                                key={index}
                                className={`${ROOT_CLASSNAME}__text-capitalize`}
                              >
                                {isLast ? sold : `${sold}, `}
                              </span>
                            );
                          })}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </Flex>
        </Row>
      </Container>
    </Section>
  );
};

export default PDPInformation;
